// Shared copy and shapes for the consent UI.
//
// The login screen, the signup form and the five public booking pages all
// have to name the same operator, link the same two documents and ask for the
// same purposes. They fetch whitelabel info separately, so the one thing they
// share is this module: everything about how consent is worded lives here and
// nothing about it is retyped per page.
//
// Who the notice names matters. Under the DPDP Act the operator is the Data
// Fiduciary and DeskOS is only their processor, so the member is agreeing to
// the operator's terms, never ours. `operatorName` is the registered entity
// when the operator has configured one, because the brand on the door and the
// company on the invoice are usually not the same name.

// Mirrors accounts/legal/purposes.py REGISTRATION_PURPOSES. Only used when the
// whitelabel response carries no `legal` block, which happens against an older
// backend or a cached response. Without it the signup form would render with
// no consent boxes at all, which is the exact state this work exists to end.
export const FALLBACK_PURPOSES = [
  {
    code: 'account',
    label: 'Account and workspace services',
    description:
      'Creating your account, giving you access to the workspace, managing your bookings, invoices and support requests, and sending you the messages needed to run those services.',
    essential: true,
  },
  {
    code: 'marketing',
    label: 'Offers and updates',
    description:
      'Sending you offers, events, newsletters and product updates by email or WhatsApp. This is not needed to use the workspace.',
    essential: false,
  },
];

export const LEGAL_PATHS = {
  terms: '/legal/terms',
  privacy: '/legal/privacy',
};

export const DOC_LABELS = {
  terms: 'Terms of Service',
  privacy: 'Privacy Notice',
};

/**
 * The name that goes in front of "Terms of Service".
 *
 * `org` is a whitelabel info payload, a public page's `organization`, or
 * anything else with a name on it.
 */
export function operatorName(org) {
  const entity = String(org?.legal?.legal_entity_name || '').trim();
  if (entity) return entity;
  const brand = String(org?.name || org?.organization_name || org?.brand_name || '').trim();
  return brand || 'this workspace';
}

/** The purposes to show on a signup form, in the order they should be asked. */
export function registrationPurposes(org) {
  const list = org?.legal?.purposes;
  if (Array.isArray(list) && list.length > 0) return list;
  return FALLBACK_PURPOSES;
}

/**
 * The starting state for a consent form.
 *
 * Every box starts unticked, including the essential one. A pre-ticked box is
 * not a clear affirmative action under s.6(1), so the member has to do
 * something before there is a consent to record.
 */
export function emptyConsentState(org) {
  const state = {};
  registrationPurposes(org).forEach((p) => {
    state[p.code] = false;
  });
  return state;
}

/** Whether every essential purpose has been ticked. */
export function hasRequiredConsents(org, value) {
  return registrationPurposes(org)
    .filter((p) => p.essential)
    .every((p) => Boolean(value?.[p.code]));
}

/**
 * The `consents` array the signup API expects.
 *
 * Declines are sent as well as grants. An operator has to be able to show a
 * member was asked about marketing and said no, and silence does not show
 * that.
 */
export function consentPayload(org, value) {
  return registrationPurposes(org).map((p) => ({
    purpose: p.code,
    granted: Boolean(value?.[p.code]),
  }));
}

/**
 * Turn a consent-notice response into the shape this kit consumes.
 *
 * Pages that know their org by slug rather than by domain (the room
 * directory, the cafe pages) call `accounts/legal/<slug>/consent-notice/`
 * instead of whitelabel info. Same facts, different envelope, so it is
 * converted once here rather than at each call site.
 */
export function orgFromNotice(data) {
  if (!data) return null;
  return {
    name: data.organization?.name || '',
    legal: {
      legal_entity_name: data.organization?.legal_entity_name || '',
      documents: data.documents,
      purposes: data.purposes,
      grievance: data.grievance,
    },
  };
}

/** The error a signup shows when the required box is not ticked. */
export const CONSENT_REQUIRED_MESSAGE =
  'Please accept the terms and privacy notice to continue.';
